export interface FilterState {
  regions: string[];
  years: number[];
  genres: string[];
}

interface FilterSidebarProps {
  filters: FilterState;
  yearOptions: number[];
  regionOptions: string[];
  genreOptions: string[];
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

function FilterGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: (string | number)[];
  selected: (string | number)[];
  onToggle: (value: string | number) => void;
}) {
  return (
    <div className="border-b border-background-200/70 pb-5 mb-5 last:border-0 last:pb-0 last:mb-0">
      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
        <span className="w-1 h-3.5 rounded-full bg-accent-500"></span>
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={String(opt)}
              type="button"
              onClick={() => onToggle(opt)}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors cursor-pointer whitespace-nowrap ${
                active
                  ? 'bg-accent-500 text-background-50 border-accent-500'
                  : 'border-background-300/60 text-foreground-700 hover:border-accent-500/50 hover:text-accent-400'
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterSidebar({
  filters,
  yearOptions,
  regionOptions,
  genreOptions,
  onChange,
  onReset,
}: FilterSidebarProps) {
  const toggle = (key: keyof FilterState, value: string | number) => {
    const list = filters[key] as (string | number)[];
    const next = list.includes(value)
      ? list.filter((v) => v !== value)
      : [...list, value];
    onChange({ ...filters, [key]: next } as FilterState);
  };

  const hasFilter =
    filters.regions.length > 0 || filters.years.length > 0 || filters.genres.length > 0;

  return (
    <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-filter-3-line text-accent-400"></i>
          筛选条件
        </h3>
        {hasFilter && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-foreground-500 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            重置
          </button>
        )}
      </div>
      <FilterGroup
        title="地区"
        options={regionOptions}
        selected={filters.regions}
        onToggle={(v) => toggle('regions', v)}
      />
      <FilterGroup
        title="年份"
        options={yearOptions}
        selected={filters.years}
        onToggle={(v) => toggle('years', v)}
      />
      <FilterGroup
        title="类型"
        options={genreOptions}
        selected={filters.genres}
        onToggle={(v) => toggle('genres', v)}
      />
    </div>
  );
}